import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { API_V1_BASE } from '@/lib/env';
import { clearAuth, setCredentials } from '@/store/authSlice';
import type { AppDispatch } from '@/store';

type UserProfile = {
  id: string;
  name: string;
  email: string;
  companyId: string;
  companyName: string;
  teamId: string | null;
  roleId: string;
  roleName: string;
  permissions: string[];
  avatarUrl?: string;
  companySettings?: {
    currency: string;
    timezone: string;
    dateFormat: string;
  };
};

/**
 * Serialize refresh calls so concurrent requests (React Strict Mode double-mount,
 * parallel 401 retries, proactive socket refresh) do not rotate the same session twice.
 */
class RefreshMutex {
  private locked = false;
  private acquireQueue: Array<() => void> = [];
  private unlockWaiters: Array<() => void> = [];

  isLocked(): boolean {
    return this.locked;
  }

  async acquire(): Promise<() => void> {
    if (!this.locked) {
      this.locked = true;
      return () => this.release();
    }
    return new Promise<() => void>((resolve) => {
      this.acquireQueue.push(() => resolve(() => this.release()));
    });
  }

  async waitForUnlock(): Promise<void> {
    if (!this.locked) return;
    await new Promise<void>((resolve) => {
      this.unlockWaiters.push(resolve);
    });
  }

  private release(): void {
    const nextAcquire = this.acquireQueue.shift();
    if (nextAcquire) {
      nextAcquire();
      return;
    }
    this.locked = false;
    const waiters = this.unlockWaiters;
    this.unlockWaiters = [];
    waiters.forEach((waiter) => waiter());
  }
}

export const refreshMutex = new RefreshMutex();

type RefreshPayload = {
  accessToken: string;
  user: UserProfile;
  isImpersonating?: boolean;
};

function applyRefreshPayload(dispatch: AppDispatch, payload: RefreshPayload): void {
  dispatch(
    setCredentials({
      accessToken: payload.accessToken,
      user: payload.user,
      isImpersonating: Boolean(payload.isImpersonating),
    }),
  );
}

/** Call /auth/refresh once under the global mutex. Returns false when unauthenticated. */
export async function refreshAuthSession(): Promise<boolean> {
  await refreshMutex.waitForUnlock();
  const release = await refreshMutex.acquire();
  try {
    const response = await fetch(`${API_V1_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      return false;
    }

    const body = (await response.json()) as {
      data?: RefreshPayload;
    };

    if (!body.data?.accessToken || !body.data.user) {
      return false;
    }

    const { store } = await import('@/store');
    applyRefreshPayload(store.dispatch, body.data);
    return true;
  } catch {
    return false;
  } finally {
    release();
  }
}

let bootstrapPromise: Promise<boolean> | null = null;

/** App bootstrap: dedupe parallel callers and clear auth only when refresh truly fails. */
export async function bootstrapAuthSession(): Promise<boolean> {
  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      const ok = await refreshAuthSession();
      if (!ok) {
        const { store } = await import('@/store');
        store.dispatch(clearAuth());
      }
      return ok;
    })();
  }
  return bootstrapPromise;
}

export type FetchWithAuthInit = RequestInit & {
  /** Do not attach Bearer / X-Company-Id (default false). */
  skipAuth?: boolean;
  /** Do not refresh+retry on 401 (default false). */
  skipRefresh?: boolean;
};

/**
 * Authenticated fetch for non-RTK call sites (SSE streams, blob downloads, media).
 * Reads token from the Redux store at call/retry time; on 401 refreshes once and retries.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: FetchWithAuthInit = {},
): Promise<Response> {
  const { skipAuth = false, skipRefresh = false, headers: initHeaders, ...rest } = init;

  const buildHeaders = async (): Promise<Headers> => {
    const headers = new Headers(initHeaders);
    if (skipAuth) return headers;

    const { store } = await import('@/store');
    const { accessToken, user } = store.getState().auth;
    if (accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    if (user?.companyId && !headers.has('X-Company-Id')) {
      headers.set('X-Company-Id', user.companyId);
    }
    return headers;
  };

  await refreshMutex.waitForUnlock();
  let response = await fetch(input, {
    ...rest,
    credentials: rest.credentials ?? 'include',
    headers: await buildHeaders(),
  });

  if (response.status !== 401 || skipAuth || skipRefresh) {
    return response;
  }

  const refreshed = await refreshAuthSession();
  if (!refreshed) {
    const { store } = await import('@/store');
    store.dispatch(clearAuth());
    return response;
  }

  response = await fetch(input, {
    ...rest,
    credentials: rest.credentials ?? 'include',
    headers: await buildHeaders(),
  });
  return response;
}

type RawBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

/** Wrap RTK base query 401 handling to use the shared refresh mutex. */
export function createBaseQueryWithReauth(rawBaseQuery: RawBaseQuery): RawBaseQuery {
  return async (args, apiCtx, extraOptions) => {
    await refreshMutex.waitForUnlock();
    let result = await rawBaseQuery(args, apiCtx, extraOptions);

    const isAuthRoute =
      typeof args !== 'string' && typeof args.url === 'string'
        ? args.url.startsWith('/auth/')
        : typeof args === 'string' && args.startsWith('/auth/');

    if (result.error?.status === 401 && !isAuthRoute) {
      if (!refreshMutex.isLocked()) {
        const release = await refreshMutex.acquire();
        try {
          const refreshResult = await rawBaseQuery(
            { url: '/auth/refresh', method: 'POST' },
            apiCtx,
            extraOptions,
          );

          const payload = (
            refreshResult.data as
              | { data?: { accessToken?: string; user?: UserProfile; isImpersonating?: boolean } }
              | undefined
          )?.data;

          if (payload?.accessToken && payload.user) {
            apiCtx.dispatch(
              setCredentials({
                accessToken: payload.accessToken,
                user: payload.user,
                isImpersonating: Boolean(payload.isImpersonating),
              }),
            );
            result = await rawBaseQuery(args, apiCtx, extraOptions);
          } else {
            apiCtx.dispatch(clearAuth());
          }
        } finally {
          release();
        }
      } else {
        await refreshMutex.waitForUnlock();
        result = await rawBaseQuery(args, apiCtx, extraOptions);
      }
    }

    return result;
  };
}
